import React, { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";

import {
    IonPage,
    IonContent,
    IonCard,
    IonCardContent,
    IonButton,
    IonLabel,
    IonItem,
    IonRadioGroup,
    IonRadio,
    IonList,
    IonAlert,
    IonModal,
} from "@ionic/react";

interface QuestionData {
    singular: string;
    options: string[];
    correctAnswer: string;
}


const OneMany: React.FC = () => {

    const [currentIndex, setCurrentIndex] = useState(0);
    const [selectedOption, setSelectedOption] = useState<string | null>(null);
    const [showImage, setShowImage] = useState(false);
    const [questions, setQuestions] = useState<QuestionData[]>([]);
    const modal = useRef<HTMLIonModalElement>(null);

    const shuffleArray = (array: any[]) => {
        for (let i = array.length - 1; i > 0; i--) {
            const randomIndex = Math.floor(Math.random() * (i + 1));
            [array[i], array[randomIndex]] = [array[randomIndex], array[i]];
        }
        return array;
    };

    const handleSubmit = (option: React.SetStateAction<string | null>) => {
        setSelectedOption(option);
        const currentQuestion = questions[currentIndex];
        if (option === currentQuestion.correctAnswer) {
            // Automatically move to the next question
            setShowImage(true);
            setTimeout(() => {
                const nextIndex = currentIndex + 1;
                if (nextIndex < questions.length) {
                    setCurrentIndex(nextIndex);
                    setShowImage(false);
                    setSelectedOption(null);
                }
                else {
                    setCurrentIndex(0);
                    fetchQuestions();
                }
            }, 5000); // Delay reduced to 3 seconds
        }
    };

    const fetchQuestions = async () => {
        try {
            let data: QuestionData[] = [];

            if (process.env.REACT_APP_DEVELOPMENT !== 'WEB') {
                const response = await fetch(process.env.REACT_APP_API_URL + 'SingularPlural.json');
                data = await response.json();
            } else {
                const module = await import('../../../datas/SingularPlural.json');
                data = module.default;
            }

            const shuffledQuestions = shuffleArray(data);
            setQuestions(shuffledQuestions);
        } catch (error) {
            console.error('Error loading data:', error);
        }
    };

    useEffect(() => {
        fetchQuestions();
        document.title = "One and Many Worksheets for UKG | Learn Singular and Plural with Fun Activities"; // Set page title
    }, []);

    return (
        <>
            <Helmet>
                <meta name="description" content='Help UKG students learn the concept of singular and plural with fun and interactive "One and Many" activities at Mama’s Guidance, designed to enhance early grammar skills.' />
            </Helmet>
            {showImage === false ? (
                <>
                    <div className="contentPart showCenter fillPreposition additionWord">
                        <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                        <IonModal
                            ref={modal}
                            trigger="open-modal"
                            initialBreakpoint={1}
                            breakpoints={[0, 0.5, 0.75, 1]}
                            handleBehavior="cycle"
                            backdropDismiss={false}  // ✅ Prevents click-to-close
                            className="full-page-modal"
                        >
                            <IonContent className="ion-padding">
                                <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                                <h1>One and Many – Singular and Plural</h1>

                                <p>
                                    Understanding the concept of <strong>one and many</strong> is an important early grammar skill. These <strong>UKG worksheets</strong> are designed to help children learn the difference between <strong>singular (one)</strong> and <strong>plural (many)</strong> through fun and engaging exercises using pictures, words, and activities.
                                </p>

                                <h2>🔠 What Is One and Many?</h2>
                                <p>
                                    <strong>“One”</strong> means a single object, while <strong>“many”</strong> means more than one. For example:
                                    <ul>
                                        <li><strong>One</strong> apple</li>
                                        <li><strong>Many</strong> apples</li>
                                    </ul>
                                    Children begin to understand how adding <strong>“s” or “es”</strong> to words changes them from singular to plural.
                                </p>

                                <h2>🎯 Learning Objectives</h2>
                                <ul>
                                    <li>Identify whether an object is singular or plural</li>
                                    <li>Add <strong>“s” or “es”</strong> to form plural words</li>
                                    <li>Match pictures with the correct word (one or many)</li>
                                    <li>Develop observation and word recognition skills</li>
                                </ul>

                                <h2>📋 Activities in the Worksheets</h2>
                                <ul>
                                    <li>Circle the correct form: one or many</li>
                                    <li>Match singular words with their plural forms</li>
                                    <li>Identify one and many from pictures</li>
                                    <li>Write the plural form of the given word</li>
                                    <li>Choose correct words from options (apple/apples)</li>
                                </ul>

                                <h2>👩‍🏫 Tips for Parents & Teachers</h2>
                                <ol>
                                    <li>Use toys or real objects to demonstrate the concept (<strong>e.g., 1 car vs. 3 cars</strong>)</li>
                                    <li>Practice plural formation rules (<strong>add ‘s’, ‘es’, and irregular forms</strong>)</li>
                                    <li>Reinforce with everyday language: <strong>“You have one pencil” vs. “You have many pencils”</strong></li>
                                    <li>Play word games like singular-plural bingo</li>
                                </ol>


                                <h2>🔗 Related Topics</h2>
                                <div>
                                    <IonItem routerLink="/ukg/english-language/articles"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Articles</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/ukg/english-language/formation-sentences"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Formation of Sentences</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/ukg/english-language/rhyming-words"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Rhyming Words</IonLabel>
                                    </IonItem>
                                </div>

                                <h2>🌟 Mama’s Guidance Tip</h2>
                                <p>
                                    Turn learning into a game — ask your child to walk around the house and collect <strong>“one spoon” or “many books.”</strong> Learning is best when it feels like play!
                                </p>

                                <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                            </IonContent>
                        </IonModal>
                        <article className="visually-hidden">
                            <h1>One and Many – Singular and Plural</h1>

                            <p>
                                Understanding the concept of <strong>one and many</strong> is an important early grammar skill. These <strong>UKG worksheets</strong> are designed to help children learn the difference between <strong>singular (one)</strong> and <strong>plural (many)</strong> through fun and engaging exercises using pictures, words, and activities.
                            </p>

                            <h2>🔠 What Is One and Many?</h2>
                            <p>
                                <strong>“One”</strong> means a single object, while <strong>“many”</strong> means more than one. For example:
                                <ul>
                                    <li><strong>One</strong> apple</li>
                                    <li><strong>Many</strong> apples</li>
                                </ul>
                                Children begin to understand how adding <strong>“s” or “es”</strong> to words changes them from singular to plural.
                            </p>

                            <h2>🎯 Learning Objectives</h2>
                            <ul>
                                <li>Identify whether an object is singular or plural</li>
                                <li>Add <strong>“s” or “es”</strong> to form plural words</li>
                                <li>Match pictures with the correct word (one or many)</li>
                                <li>Develop observation and word recognition skills</li>
                            </ul>

                            <h2>📋 Activities in the Worksheets</h2>
                            <ul>
                                <li>Circle the correct form: one or many</li>
                                <li>Match singular words with their plural forms</li>
                                <li>Identify one and many from pictures</li>
                                <li>Write the plural form of the given word</li>
                                <li>Choose correct words from options (apple/apples)</li>
                            </ul>

                            <h2>👩‍🏫 Tips for Parents & Teachers</h2>
                            <ol>
                                <li>Use toys or real objects to demonstrate the concept (<strong>e.g., 1 car vs. 3 cars</strong>)</li>
                                <li>Practice plural formation rules (<strong>add ‘s’, ‘es’, and irregular forms</strong>)</li>
                                <li>Reinforce with everyday language: <strong>“You have one pencil” vs. “You have many pencils”</strong></li>
                                <li>Play word games like singular-plural bingo</li>
                            </ol>


                            <h2>🔗 Related Topics</h2>
                            <div>
                                <IonItem routerLink="/ukg/english-language/articles"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Articles</IonLabel>
                                </IonItem>
                                <IonItem routerLink="/ukg/english-language/formation-sentences"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Formation of Sentences</IonLabel>
                                </IonItem>
                                <IonItem routerLink="/ukg/english-language/rhyming-words"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Rhyming Words</IonLabel>
                                </IonItem>
                            </div>

                            <h2>🌟 Mama’s Guidance Tip</h2>
                            <p>
                                Turn learning into a game — ask your child to walk around the house and collect <strong>“one spoon” or “many books.”</strong> Learning is best when it feels like play!
                            </p>
                        </article>
                        <p>
                            <strong>Find the word that is plural of:</strong>{" "}
                            <h3>{questions[currentIndex]?.singular}</h3>
                        </p>

                        <IonRadioGroup
                            value={selectedOption}
                            onIonChange={(e) => handleSubmit(e.detail.value!)}
                        >
                            <IonList className="grid-item-container">
                                {questions[currentIndex]?.options.map((option, index) => (
                                    <IonItem
                                        className={`grid-item ${selectedOption === option
                                            ? option === questions[currentIndex]?.correctAnswer
                                                ? "correct"
                                                : "errorItems"
                                            : ""
                                            }`}
                                        key={index}>
                                        <IonLabel>{option}</IonLabel>
                                        <IonRadio slot="start" value={option} />
                                    </IonItem>
                                ))}
                            </IonList>
                        </IonRadioGroup>

                    </div>
                </>
            ) : (
                <>
                    <div className="contentPart showMiddle">
                        <img
                            style={{ height: "150px" }}
                            className="img-fluid"
                            src={process.env.REACT_APP_WELDONE_LINK}
                        />
                    </div>
                </>
            )}
        </>
    );
};

export default OneMany;
